import {head, last, takeWhile} from 'lodash';

import type {AnnotationRange} from './annotationTypes';
import {BLOCK_TAGS} from './blockLevelElements';

/** A run of normalized characters mapped 1:1 to consecutive characters of one Text node. */
export interface ProjectionSegment {
  node: Text;
  nodeStart: number;
  normStart: number;
  length: number;
}

/**
 * A region's normalized visible text plus the mapping back to its Text nodes —
 * a pure function of the DOM, so capture and paint always agree. Segment gaps
 * are synthesized block separators with no DOM source.
 */
export interface TextProjection {
  text: string;
  segments: ProjectionSegment[];
}

const SKIPPED_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT']);

const ANNOTATE_IGNORE_ATTRIBUTE = 'data-annotate-ignore';
export const ANNOTATION_REGION_ATTRIBUTE = 'data-annotation-region';

/** Spread onto chrome inside annotatable regions: text leaves the anchors, paint is blanked. */
export const ANNOTATE_IGNORE_PROPS = {[ANNOTATE_IGNORE_ATTRIBUTE]: 'true'} as const;

/**
 * Spread onto a region's container: a region nested in another leaves the host's
 * anchors, but unlike chrome its own marks still paint.
 */
export const ANNOTATION_REGION_PROPS = {[ANNOTATION_REGION_ATTRIBUTE]: 'true'} as const;

function isMathJaxElement(element: Element): boolean {
  if (element.tagName.toLowerCase().startsWith('mjx-')) {
    return true;
  }
  return /(^|\s)mjx-/.test(element.getAttribute('class') ?? '');
}

// MathJax's invisible duplicates: v2's assistive MathML and emptied preview,
// and the staged-v4 assistive element. The visible glyphs are aria-hidden.
function isHiddenMathCopy(element: Element): boolean {
  return (
    element.tagName === 'MJX-ASSISTIVE-MML' ||
    element.classList.contains('MJX_Assistive_MathML') ||
    element.classList.contains('MathJax_Preview')
  );
}

function isExcludedElement(element: Element): boolean {
  if (SKIPPED_TAGS.has(element.tagName)) {
    return true;
  }
  if (
    element.hasAttribute(ANNOTATE_IGNORE_ATTRIBUTE) ||
    element.hasAttribute(ANNOTATION_REGION_ATTRIBUTE) ||
    element.hasAttribute('hidden')
  ) {
    return true;
  }
  if (isHiddenMathCopy(element) || element.classList.contains('sr-only')) {
    return true;
  }
  // AT reads the assistive math copy, so the visible mjx-* glyphs are carved out.
  return element.getAttribute('aria-hidden') === 'true' && !isMathJaxElement(element);
}

// Attributes the exclusion filter reads; observers watch them to invalidate
// cached projections. Class-based rules (.sr-only, MathJax) are accepted-stale.
export const FILTERED_ATTRIBUTES = [
  ANNOTATE_IGNORE_ATTRIBUTE,
  ANNOTATION_REGION_ATTRIBUTE,
  'hidden',
  'aria-hidden'
];

/** True when the node is inside `container` and outside every excluded subtree. */
export function isNodeAnnotatable(node: Node, container: Element): boolean {
  let element = node instanceof Element ? node : node.parentElement;
  while (element !== null && element !== container) {
    if (isExcludedElement(element)) {
      return false;
    }
    element = element.parentElement;
  }
  return element === container;
}

/** A pre-order walk can leave a block without entering another (`<div><p>a</p></div>tail`). */
function exitsBlock(previous: Text, current: Text, container: Element): boolean {
  let parent = previous.parentElement;
  while (parent && parent !== container) {
    // Once an ancestor contains `current`, every higher one does too.
    if (parent.contains(current)) {
      return false;
    }
    if (BLOCK_TAGS.has(parent.tagName)) {
      return true;
    }
    parent = parent.parentElement;
  }
  return false;
}

export interface AnnotatableTextNode {
  node: Text;
  blockBoundaryBefore: boolean;
}

/**
 * The container's annotatable Text nodes in document order — excluded subtrees
 * pruned wholesale — each flagged when a block boundary precedes it.
 */
export function collectAnnotatableTextNodes(container: Element): AnnotatableTextNode[] {
  const walker = document.createTreeWalker(
    container,
    NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT,
    {
      acceptNode(node) {
        if (node instanceof Element && isExcludedElement(node)) {
          return NodeFilter.FILTER_REJECT;
        }
        return NodeFilter.FILTER_ACCEPT;
      }
    }
  );

  const entries: AnnotatableTextNode[] = [];
  let sawBlockElement = false;
  let previous: Text | null = null;
  for (let current = walker.nextNode(); current !== null; current = walker.nextNode()) {
    if (current instanceof Element) {
      sawBlockElement ||= BLOCK_TAGS.has(current.tagName);
      continue;
    }
    if (!(current instanceof Text)) {
      continue;
    }
    entries.push({
      node: current,
      blockBoundaryBefore:
        sawBlockElement || (previous !== null && exitsBlock(previous, current, container))
    });
    sawBlockElement = false;
    previous = current;
  }
  return entries;
}

interface ProjectionState {
  text: string;
  segments: ProjectionSegment[];
  pendingWsSource: {node: Text; offset: number} | null;
  pendingBoundary: boolean;
}

/**
 * Emits the at-most-one pending space between real characters — source-mapped
 * when real whitespace was seen, else a synthesized separator with no segment.
 */
function flushPendingSpace(state: ProjectionState): void {
  const {pendingWsSource, pendingBoundary} = state;
  state.pendingWsSource = null;
  state.pendingBoundary = false;
  if (state.text.length === 0 || (pendingWsSource === null && !pendingBoundary)) {
    return;
  }
  if (pendingWsSource !== null) {
    state.segments.push({
      node: pendingWsSource.node,
      nodeStart: pendingWsSource.offset,
      normStart: state.text.length,
      length: 1
    });
  }
  state.text += ' ';
}

function appendTextNode(state: ProjectionState, node: Text): void {
  let openSegment: ProjectionSegment | null = null;
  for (let i = 0; i < node.data.length; i++) {
    const char = node.data[i];
    if (/\s/.test(char)) {
      state.pendingWsSource ??= {node, offset: i};
      openSegment = null;
      continue;
    }
    // A pending space can only exist when openSegment is already null.
    flushPendingSpace(state);
    if (openSegment === null) {
      openSegment = {node, nodeStart: i, normStart: state.text.length, length: 0};
      state.segments.push(openSegment);
    }
    state.text += char;
    openSegment.length += 1;
  }
}

/**
 * Builds the normalized projection: whitespace runs (NBSP included) and block
 * boundaries collapse to at most one space between real characters; edges trim.
 */
export function buildTextProjection(container: Element): TextProjection {
  const state: ProjectionState = {
    text: '',
    segments: [],
    pendingWsSource: null,
    pendingBoundary: false
  };
  for (const {node, blockBoundaryBefore} of collectAnnotatableTextNodes(container)) {
    state.pendingBoundary ||= blockBoundaryBefore;
    appendTextNode(state, node);
  }
  return {text: state.text, segments: state.segments};
}

function collapsedRangeAt(node: Node, offset: number): Range {
  const range = document.createRange();
  const maxOffset = node instanceof Text ? node.length : offset;
  range.setStart(node, Math.min(offset, maxOffset));
  range.collapse(true);
  return range;
}

/** Normalized index of the first emitted source character at or after the DOM point. */
export function normalizedStartAt(projection: TextProjection, node: Node, offset: number): number {
  const within = projection.segments.find(
    (segment) => segment.node === node && offset < segment.nodeStart + segment.length
  );
  if (within) {
    return within.normStart + Math.max(offset - within.nodeStart, 0);
  }
  const point = collapsedRangeAt(node, offset);
  const atOrAfter = projection.segments.find(
    (segment) => point.comparePoint(segment.node, segment.nodeStart) !== -1
  );
  return atOrAfter ? atOrAfter.normStart : projection.text.length;
}

/** Normalized index just past the last emitted source character strictly before the DOM point. */
export function normalizedEndAt(projection: TextProjection, node: Node, offset: number): number {
  const within = projection.segments.find(
    (segment) =>
      segment.node === node &&
      offset > segment.nodeStart &&
      offset <= segment.nodeStart + segment.length
  );
  if (within) {
    return within.normStart + (offset - within.nodeStart);
  }
  // Not inside any mapped run, so every segment lies entirely before or after the point.
  const point = collapsedRangeAt(node, offset);
  const before = takeWhile(
    projection.segments,
    (segment) => point.comparePoint(segment.node, segment.nodeStart) === -1
  );
  const lastBefore = last(before);
  return lastBefore ? lastBefore.normStart + lastBefore.length : 0;
}

/**
 * Converts a DOM Range to normalized offsets, snapping inward (captured text is
 * always a subset of the selection). Null when nothing annotatable is covered.
 */
export function domRangeToNormalized(
  projection: TextProjection,
  range: Range
): AnnotationRange | null {
  if (projection.segments.length === 0) {
    return null;
  }
  const start = normalizedStartAt(projection, range.startContainer, range.startOffset);
  const end = normalizedEndAt(projection, range.endContainer, range.endOffset);
  if (start >= end) {
    return null;
  }
  return {start, end};
}

/**
 * Inverse of `domRangeToNormalized`: the Range covers `[start, end)`'s first
 * through last source-mapped characters. Null when out of bounds or none exist.
 */
export function normalizedRangeToDomRange(
  projection: TextProjection,
  {start, end}: AnnotationRange
): Range | null {
  if (start < 0 || end > projection.text.length || start >= end) {
    return null;
  }
  const overlapping = projection.segments.filter(
    (segment) => segment.normStart < end && segment.normStart + segment.length > start
  );
  const firstSegment = head(overlapping);
  const lastSegment = last(overlapping);
  if (!firstSegment || !lastSegment) {
    return null;
  }
  const range = document.createRange();
  range.setStart(
    firstSegment.node,
    firstSegment.nodeStart + Math.max(start - firstSegment.normStart, 0)
  );
  range.setEnd(
    lastSegment.node,
    lastSegment.nodeStart +
      Math.min(end, lastSegment.normStart + lastSegment.length) -
      lastSegment.normStart
  );
  return range;
}
